import { useMemo } from 'react';

import { useContactGroupActions } from 'contacts/hooks';
import { IContactGroup, IContactGroupSettingsForm } from 'contacts/types';
import { FormProvider, useNotificationContext } from 'core/states';

import { ContactGroupFormFields } from './ContactGroupSettingsFormFields';
import { contactGroupSchema } from './schema';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  contactGroup?: IContactGroup;
  onSubmitSuccess?: (contactGroup: IContactGroup) => void;
}

export const UpdateContactGroup = ({ isOpen, onClose, contactGroup, onSubmitSuccess }: IProps) => {
  const { clearNotification } = useNotificationContext();

  const { updateContactGroup } = useContactGroupActions();

  const defaultValues = useMemo(() => {
    return {
      accountsReceivableId: contactGroup.accountsReceivableId || '',
      contactGrouping:
        contactGroup.contactGroupingId && contactGroup.contactGroupingName
          ? { id: contactGroup.contactGroupingId, name: contactGroup.contactGroupingName }
          : null,
      domains: (contactGroup.domains || []).map((domain: string) => ({
        id: domain,
        title: domain,
      })),
      externalCustomerNumber: contactGroup.externalCustomerNumber || '',
      isCustomer: contactGroup.isCustomer === 1,
      isVendor: contactGroup.isVendor === 1,
      automatedPricing: contactGroup.automatedPricing === 1,
      autoLeadTimeEnabled: contactGroup.autoLeadTimeEnabled === 1,
      freightEnabled: contactGroup.freightEnabled === 1,
      showInventory: contactGroup.showInventory === 1,
      vendorId: contactGroup.legacyVendorNumbers?.[0] || undefined,
      vendorName: contactGroup.legacyVendorName || '',
      alternateVendorId: contactGroup.alternateVendorId || undefined,
      alternateVendorName: contactGroup.alternateVendorName || '',
    };
  }, [contactGroup]);

  const onSubmit = () => async (data: IContactGroupSettingsForm) => {
    const contactGroupData: Partial<IContactGroup> = {
      accountsReceivableId: data.accountsReceivableId,
      id: contactGroup.id,
      contactGroupingId: data.contactGrouping?.id || null,
      domains:
        data.domains?.length > 0
          ? data.domains.map((domain: { id: string; title: string }) => domain.id)
          : null,
      externalCustomerNumber: data.externalCustomerNumber,
      isCustomer: data.isCustomer ? 1 : 0,
      isVendor: data.isVendor ? 1 : 0,
      automatedPricing: data.automatedPricing ? 1 : 0,
      autoLeadTimeEnabled: data.autoLeadTimeEnabled ? 1 : 0,
      freightEnabled: data.freightEnabled ? 1 : 0,
      showInventory: data.showInventory ? 1 : 0,
    };

    if (data.isVendor) {
      contactGroupData.vendorId = data.vendorId;
      contactGroupData.vendorName = data.vendorName;
      contactGroupData.alternateVendorId = data.alternateVendorId;
      contactGroupData.alternateVendorName = data.alternateVendorName;
    }

    const { success, contactGroup: updatedContactGroup } = await updateContactGroup({
      contactGroup: contactGroupData,
    });

    if (!success) {
      return false;
    }

    onSubmitSuccess?.({
      ...updatedContactGroup,
      legacyVendorNumbers: [updatedContactGroup.vendorId],
      legacyVendorName: updatedContactGroup.vendorName,
    });

    setTimeout(() => handleOnClose(), 1000);
  };

  const handleOnClose = () => {
    clearNotification();
    onClose();
  };

  return (
    <FormProvider schema={contactGroupSchema()} defaultValues={defaultValues} resetDefaultValues>
      <ContactGroupFormFields isOpen={isOpen} onClose={handleOnClose} onSubmit={onSubmit} />
    </FormProvider>
  );
};
